import React, { useEffect, useRef, useState } from "react";
import { Box, Card } from "@mui/material";
import videojs from "video.js";
import Player from "video.js/dist/types/player";
import "video.js/dist/video-js.css";

import { EditorialAsset } from "@/types/editorialAssetsTypes";

interface EditorialAssetProps {
    editorialAsset: EditorialAsset;
    selected?: boolean;
    onClick?: () => void;
    onPreviewClick?: () => void;
    cardWidth?: string | number | Record<string, string | number>;
}

const EditorialAssetCard: React.FC<EditorialAssetProps> = ({
                                                               editorialAsset,
                                                               selected = false,
                                                               onClick = () => {},
                                                               onPreviewClick = () => {},
                                                               cardWidth = "30%"
                                                           }) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const playerRef = useRef<Player | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);

    useEffect(() => {
        if (!playerRef.current && videoRef.current) {
            playerRef.current = videojs(videoRef.current, {
                controls: false,
                responsive: true,
                fluid: true,
                sources: [{
                    src: editorialAsset.preview_path,
                    type: "video/mp4"
                }]
            });

            playerRef.current.on("play", () => setIsPlaying(true));
            playerRef.current.on("pause", () => setIsPlaying(false));
            playerRef.current.on("ended", () => setIsPlaying(false));
        }

        return () => {
            if (playerRef.current && !playerRef.current.isDisposed()) {
                playerRef.current.dispose();
                playerRef.current = null;
            }
        };
    }, [editorialAsset]);

    // Gestione dell'Hover (Entrata)
    const handleMouseEnter = () => {
        onPreviewClick(); // Opzionale: richiama la prop se vuoi notificare il parent

        if (playerRef.current) {
            const playPromise = playerRef.current.play();

            // Gestiamo la promise per evitare l'errore "The play() request was interrupted by a call to pause()"
            if (playPromise !== undefined) {
                playPromise.catch((error) => {
                    console.error("Autoplay paused when the mouse moves away:", error);
                });
            }
        }
    };

    // Gestione dell'Hover (Uscita)
    const handleMouseLeave = () => {
        if (playerRef.current) {
            playerRef.current.pause();
            playerRef.current.currentTime(0); // Questo fa il vero e proprio "stop", resettando il video all'inizio
        }
    };

    return (
        <Box
            key={editorialAsset.id}
            onClick={onClick}
            sx={{
                perspective: "1000px",
                width: cardWidth,
                flexShrink: 0,
            }}
        >
            <Card
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                sx={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "16 / 9",
                    borderRadius: 1,
                    overflow: "hidden",
                    cursor: "pointer",
                    border: selected ? "2px solid white" : "2px solid transparent",
                    backgroundColor: "black",
                    "& .vjs-big-play-button": {
                        display: "none"
                    }
                }}
            >
                <Box sx={{ width: "100%", height: "100%", overflow: "hidden", pointerEvents: "none" }}>
                    <div data-vjs-player style={{ height: "100%", width: "100%", overflow: "hidden" }}>
                        {editorialAsset && editorialAsset.placement_type !== "watermark" && 
                            <video
                                ref={videoRef}
                                className="video-js vjs-fill"
                                muted
                            />
                        }
                    </div>
                </Box>

                {!isPlaying && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            backgroundImage: `url(${editorialAsset.thumbnail_path})`,
                            backgroundSize: "contain",
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: "center",
                            zIndex: 5,
                            pointerEvents: "none",
                        }}
                    />
                )}
            </Card>
        </Box>
    );
};

export default EditorialAssetCard;